import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Search,
  CalendarDays,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import AppSelect from "../ui/app-select";
import InstitutionLogo from "../InstitutionLogo";
import { formatDate } from "../../utils/api";

const STATUS_LABELS = {
  event_created: "Created",
  event_scheduled: "Scheduled",
  shoot_completed: "Shoot complete",
  delivery_in_progress: "In delivery",
  closed: "Closed",
};
const PAGE_SIZE = 5;
const time = (date) => new Date(date || 0).getTime() || 0;

function EventStatus({ status }) {
  return (
    <span
      className={`event-status status-${status}`}
      data-testid="event-status-badge"
    >
      <i />
      {STATUS_LABELS[status] || status?.replaceAll("_", " ") || "Not set"}
    </span>
  );
}

export default function RecentEvents({ events }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const filtered = [...events]
    .sort((a, b) => time(b.created_at) - time(a.created_at))
    .filter(
      (event) =>
        `${event.title} ${event.institution_name || ""}`
          .toLowerCase()
          .includes(search.trim().toLowerCase()) &&
        (status === "all" ||
          (status === "closed"
            ? event.status === "closed"
            : event.status !== "closed")),
    );
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * PAGE_SIZE;
  const visible = filtered.slice(offset, offset + PAGE_SIZE);
  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setPage(1);
  };

  return (
    <section
      className="dashboard-panel recent-panel"
      aria-labelledby="recent-title"
      data-testid="recent-events-card"
    >
      <div className="panel-heading">
        <div>
          <h2 id="recent-title">
            Recent events <span className="count-pill">{events.length}</span>
          </h2>
          <p>Latest additions to your workspace</p>
        </div>
        <Link className="text-action" to="/events">
          View all <ArrowRight size={16} />
        </Link>
      </div>
      <div className="events-toolbar">
        <div className="event-search">
          <Search size={18} aria-hidden="true" />
          <input
            aria-label="Search recent events"
            type="search"
            placeholder="Search events or institutions"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              aria-label="Clear event search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <AppSelect
          aria-label="Filter recent events by status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All statuses" },
            { value: "open", label: "Open" },
            { value: "closed", label: "Closed" },
          ]}
        />
      </div>
      {visible.length > 0 ? (
        <>
          <div className="recent-table-scroll">
            <table className="recent-table">
              <caption className="sr-only">
                Events, most recently created first
              </caption>
              <thead>
                <tr>
                  <th scope="col">Event / Institution</th>
                  <th scope="col">Event date</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((event) => (
                  <tr key={event.id} data-testid={`event-card-${event.id}`}>
                    <td>
                      <div className="event-name-cell">
                        <InstitutionLogo
                          name={event.institution_name}
                          size={36}
                          className="event-monogram"
                        />
                        <div>
                          <Link to={`/events/${event.id}`}>{event.title}</Link>
                          <span>
                            {event.institution_name || "No institution"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="event-date">
                      {formatDate(event.event_date_start) || "Not scheduled"}
                    </td>
                    <td>
                      <EventStatus status={event.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="mobile-events" aria-label="Recent events">
            {visible.map((event) => (
              <li key={event.id}>
                <Link className="mobile-event-link" to={`/events/${event.id}`}>
                  <strong>{event.title}</strong>
                  <ChevronRight size={18} />
                </Link>
                <p>{event.institution_name || "No institution"}</p>
                <div>
                  <span>
                    {formatDate(event.event_date_start) || "Not scheduled"}
                  </span>
                  <EventStatus status={event.status} />
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="dashboard-empty">
          <CalendarDays size={28} />
          <h3>{events.length ? "No matching events" : "No events yet"}</h3>
          <p>
            {events.length
              ? "Try a different search or clear your filters."
              : "Create your first event to start planning."}
          </p>
          {events.length ? (
            <Button variant="outline" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : (
            <Button asChild>
              <Link to="/events?create=new">Create event</Link>
            </Button>
          )}
        </div>
      )}
      <div className="table-footer">
        <span role="status" aria-live="polite">
          {filtered.length
            ? `${offset + 1}–${offset + visible.length} of ${filtered.length} events`
            : "0 events"}
        </span>
        <nav className="table-pagination" aria-label="Recent events pages">
          <button
            aria-label="Previous events page"
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            {currentPage} / {pageCount}
          </span>
          <button
            aria-label="Next events page"
            disabled={currentPage === pageCount}
            onClick={() => setPage(currentPage + 1)}
          >
            <ChevronRight size={16} />
          </button>
        </nav>
      </div>
    </section>
  );
}
